import React, { useState, useEffect, useMemo } from "react";
import "../styles/TerminePage.css";
import { getTermine, getTermineCategories, getTerminePage } from "../lib/api";
import { Page, PageHeader } from "../components/ui";

const MONTHS = ["Jänner","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const WEEKDAYS = ["Mo","Di","Mi","Do","Fr","Sa","So"];

// Local date → "YYYY-MM-DD". Do NOT use toISOString() here: it converts
// to UTC, so local midnight in Vienna becomes the previous day.
function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// "YYYY-MM-DD" → local Date. new Date("YYYY-MM-DD") would be parsed as
// UTC midnight, which shifts the day in timezones behind UTC.
function parseISODate(iso) {
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(y, m - 1, d);
}

function formatLongDate(iso) {
  const d = parseISODate(iso);
  return `${d.getDate()}. ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export default function TerminePage() {
  const [page, setPage] = useState(null);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [view, setView] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 721 ? "list" : "calendar"
  );
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeCategory, setActiveCategory] = useState("Alle");

  useEffect(() => {
    Promise.all([getTerminePage(), getTermine(), getTermineCategories()])
      .then(([pageRes, eventsRes, catsRes]) => {
        setPage(pageRes);
        setEvents(eventsRes);
        setCategories(catsRes);
      })
      .catch((err) => {
        console.error(err);
        setError(err);
      });
  }, []);

  const catMap = useMemo(() => {
    const m = {};
    categories.forEach((c) => { m[c.name] = c.color || "#925a64"; });
    return m;
  }, [categories]);

  const filteredEvents = useMemo(() =>
    activeCategory === "Alle" ? events : events.filter((e) => e.category === activeCategory),
    [events, activeCategory]
  );

  const monthEvents = useMemo(() => {
    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth();
    return filteredEvents
      .filter((e) => { const d = parseISODate(e.date); return d.getFullYear() === y && d.getMonth() === m; })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredEvents, currentMonth]);

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startWeekday = (first.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push({ type: "empty", key: `es-${i}` });
    for (let day = 1; day <= last.getDate(); day++) {
      const iso = toISODate(new Date(year, month, day));
      const dayEvents = filteredEvents.filter((e) => e.date === iso);
      const dow = (new Date(year, month, day).getDay() + 6) % 7;
      cells.push({ type: "day", key: iso, day, iso, events: dayEvents, isWeekend: dow >= 5 });
    }
    while (cells.length % 7 !== 0) cells.push({ type: "empty", key: `ee-${cells.length}` });
    return cells;
  }, [filteredEvents, currentMonth]);

  const selectedEvents = useMemo(() => {
    if (!selectedDate) return [];
    return filteredEvents.filter((e) => e.date === selectedDate);
  }, [filteredEvents, selectedDate]);

  const monthLabel = `${MONTHS[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;

  return (
    <Page className="term-page" loading={!page} error={error}>
      <PageHeader
        parent={{ label: "Aktuelles", to: "/einblicke" }}
        current="Termine"
        title={page?.heroTitle || "Termine"}
        subtitle={page?.heroSubtitle}
      />

      <section className="term-section">
        <div className="container">
          <div className="term-filters" role="tablist" aria-label="Kategorien filtern">
            <button
              type="button"
              className={`term-filter-chip ${activeCategory === "Alle" ? "is-active" : ""}`}
              onClick={() => { setActiveCategory("Alle"); setSelectedDate(null); }}
            >
              Alle
            </button>
            {categories.map((cat) => {
              const isActive = activeCategory === cat.name;
              return (
                <button
                  key={cat.id}
                  type="button"
                  className={`term-filter-chip ${isActive ? "is-active" : ""}`}
                  style={isActive ? { background: `${cat.color}20`, borderColor: `${cat.color}60`, color: cat.color } : {}}
                  onClick={() => { setActiveCategory(isActive ? "Alle" : cat.name); setSelectedDate(null); }}
                >
                  <span className="term-filter-chip__dot" style={{ background: cat.color }} />
                  {cat.name}
                </button>
              );
            })}
          </div>

          <div className="term-toolbar">
            <div className="term-monthnav">
              <button type="button" className="term-iconbtn" onClick={() => { setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)); setSelectedDate(null); }} aria-label="Vorheriger Monat"><IconChevronLeft /></button>
              <div className="term-monthlabel">{monthLabel}</div>
              <button type="button" className="term-iconbtn" onClick={() => { setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)); setSelectedDate(null); }} aria-label="Nächster Monat"><IconChevronRight /></button>
            </div>
            <div className="term-viewtoggle" role="tablist">
              <button type="button" className={`term-toggle ${view === "calendar" ? "is-active" : ""}`} onClick={() => setView("calendar")} role="tab" aria-selected={view === "calendar"}><IconCalendar /><span>Kalender</span></button>
              <button type="button" className={`term-toggle ${view === "list" ? "is-active" : ""}`} onClick={() => setView("list")} role="tab" aria-selected={view === "list"}><IconList /><span>Liste</span></button>
            </div>
          </div>

          {view === "calendar" ? (
            <div className="term-layout">
              <div className="term-calendarCard">
                <div className="term-weekdays">
                  {WEEKDAYS.map((d) => <div className="term-weekday" key={d}>{d}</div>)}
                </div>
                <div className="term-grid">
                  {calendarDays.map((cell) => {
                    if (cell.type === "empty") return <div key={cell.key} className="term-day term-day--empty" />;
                    const isSelected = selectedDate === cell.iso;
                    return (
                      <button key={cell.key} type="button"
                        className={`term-day ${isSelected ? "is-selected" : ""} ${cell.events.length > 0 ? "has-events" : ""} ${cell.isWeekend ? "is-weekend" : ""}`}
                        onClick={() => setSelectedDate(cell.iso)}
                      >
                        <span className="term-day__number">{cell.day}</span>
                        <div className="term-day__dots">
                          {cell.events.slice(0, 3).map((e) => (
                            <span key={e.id} className="term-dot" style={{ background: catMap[e.category] || "#925a64" }} title={e.title} />
                          ))}
                          {cell.events.length > 3 ? <span className="term-dot term-dot--more" /> : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <aside className="term-sideCard">
                <div className="term-sideCard__head">
                  <div className="term-badge"><IconPin /><span>Ausgewählter Tag</span></div>
                  <h2>{selectedDate ? formatLongDate(selectedDate) : "Bitte einen Tag auswählen"}</h2>
                  {!selectedDate ? <p>Klicke im Kalender auf einen Tag.</p> : null}
                </div>
                <div className="term-sideCard__body">
                  {selectedDate && selectedEvents.length === 0 ? (
                    <div className="term-emptyState"><IconCalendarLarge /><p>Keine Termine an diesem Tag.</p></div>
                  ) : null}
                  {selectedEvents.length > 0 ? (
                    <div className="term-eventList">
                      {selectedEvents.map((e) => <EventCard key={e.id} event={e} color={catMap[e.category]} />)}
                    </div>
                  ) : null}
                  {!selectedDate ? <div className="term-emptyState"><IconCalendarLarge /><p>Wähle einen Tag im Kalender aus.</p></div> : null}
                </div>
              </aside>
            </div>
          ) : (
            <div className="term-listCard">
              <div className="term-listHeader">
                <h2>Termine im {monthLabel}</h2>
                <p>Alle Termine dieses Monats in chronologischer Reihenfolge.</p>
              </div>
              <div className="term-list">
                {monthEvents.length === 0 ? (
                  <div className="term-emptyState term-emptyState--large"><IconCalendarLarge /><p>Für diesen Monat sind keine Termine eingetragen.</p></div>
                ) : monthEvents.map((event) => {
                  const d = parseISODate(event.date);
                  const color = catMap[event.category];
                  return (
                    <article className="term-listItem" key={event.id}>
                      <div className="term-listItem__date" style={{ borderColor: color ? `${color}40` : undefined }}>
                        <span style={{ color: color || "#925a64" }}>{d.getDate()}</span>
                        <small>{MONTHS[d.getMonth()].slice(0, 3)}</small>
                      </div>
                      <div className="term-listItem__body">
                        <div className="term-listItem__meta">
                          <span className="term-pill" style={{ background: color ? `${color}18` : undefined, color: color || "#925a64" }}>{event.category}</span>
                          <span className="term-time">{event.time}</span>
                        </div>
                        <h3>{event.title}</h3>
                        <p>{event.description}</p>
                        {event.location && event.location !== "—" ? (
                          <div className="term-listItem__footer">
                            <span className="term-location"><IconLocation />{event.location}</span>
                          </div>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>
    </Page>
  );
}

function EventCard({ event, color }) {
  return (
    <article className="term-eventCard">
      <div className="term-eventCard__meta">
        <span className="term-pill" style={{ background: color ? `${color}18` : undefined, color: color || "#925a64" }}>{event.category}</span>
        <span className="term-time">{event.time}</span>
      </div>
      <h3>{event.title}</h3>
      <p>{event.description}</p>
      {event.location && event.location !== "—" ? (
        <div className="term-eventCard__footer">
          <span className="term-location"><IconLocation />{event.location}</span>
        </div>
      ) : null}
    </article>
  );
}

function Svg({ children, size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">{children}</svg>;
}
function IconChevronLeft() { return <Svg><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" /></Svg>; }
function IconChevronRight() { return <Svg><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" /></Svg>; }
function IconCalendar() { return <Svg><path d="M7 3v3M17 3v3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><path d="M4 7h16v14H4V7z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><path d="M4 11h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></Svg>; }
function IconList() { return <Svg><path d="M9 7h10M9 12h10M9 17h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><circle cx="5" cy="7" r="1.2" fill="currentColor" /><circle cx="5" cy="12" r="1.2" fill="currentColor" /><circle cx="5" cy="17" r="1.2" fill="currentColor" /></Svg>; }
function IconPin() { return <Svg><path d="M12 21s6-5 6-10a6 6 0 10-12 0c0 5 6 10 6 10z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><circle cx="12" cy="11" r="2" stroke="currentColor" strokeWidth="1.7" /></Svg>; }
function IconLocation() { return <Svg size={16}><path d="M8 14s4-3.3 4-6.7A4 4 0 100 7.3C0 10.7 4 14 4 14z" transform="translate(8 4)" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></Svg>; }
function IconCalendarLarge() { return <Svg size={36}><path d="M7 3v3M17 3v3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><path d="M4 7h16v14H4V7z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><path d="M4 11h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></Svg>; }